import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useConstants } from '@/contexts/ConstantsContext';
import { useAuth } from '@/contexts/AuthContext';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { ScrollArea } from '@/components/ui/scroll-area';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EditLeadModal = ({ open, onOpenChange, lead, onSuccess }) => {
  const { industries, country_markets, icp_fits, lead_sources, next_step_types, company_sizes, priorities, preferred_phones, best_times_to_call, users, closed_lost_reasons, sectors, getNichesForSector } = useConstants();
  const { isAdmin, isSetter } = useAuth();

  const [formData, setFormData] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (lead) {
      setFormData({
        full_name: lead.full_name || '',
        company_name: lead.company_name || '',
        phone: lead.phone || '',
        owner_id: lead.owner_id || '',
        country_market: lead.country_market || '',
        industry: lead.industry || '',
        sector: lead.sector || '',
        niche: lead.niche || '',
        icp_fit: lead.icp_fit || '',
        lead_source: lead.lead_source || '',
        next_step_type: lead.next_step_type || 'Discovery',
        email: lead.email || '',
        linkedin_url: lead.linkedin_url || '',
        website: lead.website || '',
        city_state: lead.city_state || '',
        job_title: lead.job_title || '',
        company_size: lead.company_size || '',
        source_detail: lead.source_detail || '',
        priority: lead.priority || 'Medium',
        deal_value_estimate: lead.deal_value_estimate || '',
        notes: lead.notes || '',
        preferred_phone: lead.preferred_phone || 'Unknown',
        best_time_to_call: lead.best_time_to_call || 'Unknown',
        use_case: lead.use_case || '',
        current_tools: lead.current_tools || '',
        do_not_call: lead.do_not_call || false,
        do_not_call_reason: lead.do_not_call_reason || '',
        closed_lost_reason: lead.closed_lost_reason || '',
      });
    }
  }, [lead]);

  // Get niches for selected sector
  const availableNiches = formData.sector ? getNichesForSector(formData.sector) : [];

  const handleChange = (field, value) => {
    setFormData(prev => {
      const newData = { ...prev, [field]: value };
      // Reset niche when sector changes
      if (field === 'sector') {
        newData.niche = '';
      }
      return newData;
    });
  };

  const handleSubmit = async () => {
    setSaving(true);
    try {
      const data = {
        ...formData,
        deal_value_estimate: formData.deal_value_estimate ? parseFloat(formData.deal_value_estimate) : null,
      };
      await axios.put(`${API}/leads/${lead.id}`, data);
      toast.success('Lead updated');
      onOpenChange(false);
      if (onSuccess) onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update lead');
    } finally {
      setSaving(false);
    }
  };

  if (!lead) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl bg-card border-border max-h-[90vh]" data-testid="edit-lead-modal">
        <DialogHeader>
          <DialogTitle>Edit Lead</DialogTitle>
        </DialogHeader>

        <ScrollArea className="h-[calc(90vh-120px)] pr-4">
          <div className="space-y-6 py-4">
            {/* Basic Info */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Full Name</Label>
                <Input
                  value={formData.full_name}
                  onChange={(e) => handleChange('full_name', e.target.value)}
                  className="bg-secondary border-border"
                />
              </div>
              <div className="space-y-2">
                <Label>Company Name</Label>
                <Input
                  value={formData.company_name}
                  onChange={(e) => handleChange('company_name', e.target.value)}
                  className="bg-secondary border-border"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Phone</Label>
                <Input
                  value={formData.phone}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  className="bg-secondary border-border"
                />
              </div>
              <div className="space-y-2">
                <Label>Owner</Label>
                <Select 
                  value={formData.owner_id} 
                  onValueChange={(v) => handleChange('owner_id', v)}
                  disabled={isSetter}
                >
                  <SelectTrigger className="bg-secondary border-border">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {users.map(u => (
                      <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {isSetter && (
                  <p className="text-xs text-muted-foreground">Setters cannot reassign leads</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Market</Label>
                <Select value={formData.country_market} onValueChange={(v) => handleChange('country_market', v)}>
                  <SelectTrigger className="bg-secondary border-border">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {country_markets.map(m => (
                      <SelectItem key={m} value={m}>{m}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Industry</Label>
                <Select value={formData.industry} onValueChange={(v) => handleChange('industry', v)}>
                  <SelectTrigger className="bg-secondary border-border">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {industries.map(i => (
                      <SelectItem key={i} value={i}>{i}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Sector</Label>
                <Select value={formData.sector || "none"} onValueChange={(v) => handleChange('sector', v === "none" ? "" : v)}>
                  <SelectTrigger className="bg-secondary border-border" data-testid="edit-lead-sector">
                    <SelectValue placeholder="Select sector" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Not specified</SelectItem>
                    {sectors.map(s => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Niche</Label>
                <Select 
                  value={formData.niche || "none"} 
                  onValueChange={(v) => handleChange('niche', v === "none" ? "" : v)}
                  disabled={!formData.sector}
                >
                  <SelectTrigger className="bg-secondary border-border" data-testid="edit-lead-niche">
                    <SelectValue placeholder={formData.sector ? "Select niche" : "Select sector first"} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Not specified</SelectItem>
                    {availableNiches.map(n => (
                      <SelectItem key={n} value={n}>{n}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>ICP Fit</Label>
                <Select value={formData.icp_fit} onValueChange={(v) => handleChange('icp_fit', v)}>
                  <SelectTrigger className="bg-secondary border-border">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {icp_fits.map(f => (
                      <SelectItem key={f} value={f}>{f}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Lead Source</Label>
                <Select value={formData.lead_source} onValueChange={(v) => handleChange('lead_source', v)}>
                  <SelectTrigger className="bg-secondary border-border">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {lead_sources.map(s => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Priority</Label>
                <Select value={formData.priority} onValueChange={(v) => handleChange('priority', v)}>
                  <SelectTrigger className="bg-secondary border-border">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {priorities.map(p => (
                      <SelectItem key={p} value={p}>{p}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Deal Value</Label>
                <Input
                  type="number"
                  value={formData.deal_value_estimate}
                  onChange={(e) => handleChange('deal_value_estimate', e.target.value)}
                  className="bg-secondary border-border"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Email</Label>
                <Input
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  className="bg-secondary border-border"
                />
              </div>
              <div className="space-y-2">
                <Label>Job Title</Label>
                <Input
                  value={formData.job_title}
                  onChange={(e) => handleChange('job_title', e.target.value)}
                  className="bg-secondary border-border"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>LinkedIn URL</Label>
                <Input
                  value={formData.linkedin_url}
                  onChange={(e) => handleChange('linkedin_url', e.target.value)}
                  className="bg-secondary border-border"
                />
              </div>
              <div className="space-y-2">
                <Label>Website</Label>
                <Input
                  value={formData.website}
                  onChange={(e) => handleChange('website', e.target.value)}
                  className="bg-secondary border-border"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>City/State</Label>
                <Input
                  value={formData.city_state}
                  onChange={(e) => handleChange('city_state', e.target.value)}
                  className="bg-secondary border-border"
                />
              </div>
              <div className="space-y-2">
                <Label>Company Size</Label>
                <Select value={formData.company_size || "none"} onValueChange={(v) => handleChange('company_size', v === "none" ? "" : v)}>
                  <SelectTrigger className="bg-secondary border-border">
                    <SelectValue placeholder="Select size" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Not specified</SelectItem>
                    {company_sizes.map(s => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Preferred Phone</Label>
                <Select value={formData.preferred_phone} onValueChange={(v) => handleChange('preferred_phone', v)}>
                  <SelectTrigger className="bg-secondary border-border">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {preferred_phones.map(p => (
                      <SelectItem key={p} value={p}>{p}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Best Time to Call</Label>
                <Select value={formData.best_time_to_call} onValueChange={(v) => handleChange('best_time_to_call', v)}>
                  <SelectTrigger className="bg-secondary border-border">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {best_times_to_call.map(t => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Closed Lost Reason */}
            {lead.stage === 'Closed Lost' && (
              <div className="space-y-2">
                <Label>Closed Lost Reason</Label>
                <Select value={formData.closed_lost_reason || "none"} onValueChange={(v) => handleChange('closed_lost_reason', v === "none" ? "" : v)}>
                  <SelectTrigger className="bg-secondary border-border">
                    <SelectValue placeholder="Select reason" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Not specified</SelectItem>
                    {closed_lost_reasons.map(r => (
                      <SelectItem key={r} value={r}>{r}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Do Not Call */}
            <div className="flex items-center justify-between p-4 bg-secondary/50 rounded-lg">
              <div>
                <Label className="text-base">Do Not Call</Label>
                <p className="text-sm text-muted-foreground">Mark this lead as do not call</p>
              </div>
              <Switch
                checked={formData.do_not_call}
                onCheckedChange={(v) => handleChange('do_not_call', v)}
              />
            </div>

            {formData.do_not_call && (
              <div className="space-y-2">
                <Label>Do Not Call Reason</Label>
                <Input
                  value={formData.do_not_call_reason}
                  onChange={(e) => handleChange('do_not_call_reason', e.target.value)}
                  placeholder="Reason for DNC"
                  className="bg-secondary border-border"
                />
              </div>
            )}

            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => handleChange('notes', e.target.value)}
                className="bg-secondary border-border resize-none"
                rows={3}
              />
            </div>

            <Button
              className="w-full"
              onClick={handleSubmit}
              disabled={saving}
              data-testid="save-edit-lead"
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};

export default EditLeadModal;
